import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

export interface DiscussionMessage {
  id: string;
  project_id: string;
  author_id: string;
  content: string;
  is_pinned: boolean;
  is_deleted: boolean;
  is_edited: boolean;
  created_at: string;
  updated_at: string;
  author: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
}

export interface DiscussionResponse {
  success: boolean;
  data: {
    project: {
      id: string;
      name: string;
    };
    messages: DiscussionMessage[];
    totalMessages: number;
  };
}

export interface MessagesResponse {
  success: boolean;
  data: {
    messages: DiscussionMessage[];
    hasMore: boolean;
  };
}

export interface MessageResponse {
  success: boolean;
  data: {
    message: DiscussionMessage;
  };
}

export interface SocketTokenResponse {
  success: boolean;
  data: {
    token: string;
    socketUrl: string;
    expiresIn: number;
  };
}

// Get initial discussion data
export async function getProjectDiscussion(
  projectId: string,
  limit: number = 50
): Promise<DiscussionResponse> {
  const url = getBITSEndApiMap("getProjectDiscussion").replace(
    ":projectId",
    projectId
  );
  const response = await api.get(`${url}?limit=${limit}`);
  return response.data;
}

// Get paginated messages (for infinite scroll)
export async function getProjectDiscussionMessages(
  projectId: string,
  before?: string,
  limit: number = 50
): Promise<MessagesResponse> {
  const url = getBITSEndApiMap("getProjectDiscussionMessages").replace(
    ":projectId",
    projectId
  );
  const params = new URLSearchParams();
  params.append("limit", limit.toString());
  if (before) {
    params.append("before", before);
  }
  const response = await api.get(`${url}?${params.toString()}`);
  return response.data;
}

// Send new message
export async function sendProjectMessage(
  projectId: string,
  content: string
): Promise<MessageResponse> {
  const url = getBITSEndApiMap("sendProjectMessage").replace(
    ":projectId",
    projectId
  );
  const response = await api.post(url, { content });
  return response.data;
}

// Edit message
export async function editProjectMessage(
  projectId: string,
  messageId: string,
  content: string
): Promise<MessageResponse> {
  const url = getBITSEndApiMap("editProjectMessage")
    .replace(":projectId", projectId)
    .replace(":messageId", messageId);
  const response = await api.put(url, { content });
  return response.data;
}

// Delete message
export async function deleteProjectMessage(
  projectId: string,
  messageId: string
): Promise<{ success: boolean }> {
  const url = getBITSEndApiMap("deleteProjectMessage")
    .replace(":projectId", projectId)
    .replace(":messageId", messageId);
  const response = await api.delete(url);
  return response.data;
}

// Pin/Unpin message
export async function pinProjectMessage(
  projectId: string,
  messageId: string,
  isPinned: boolean
): Promise<MessageResponse> {
  const url = getBITSEndApiMap("pinProjectMessage")
    .replace(":projectId", projectId)
    .replace(":messageId", messageId);
  const response = await api.post(url, { isPinned });
  return response.data;
}

// Get Socket.IO connection token
export async function getDiscussionSocketToken(
  projectId: string
): Promise<SocketTokenResponse> {
  const url = getBITSEndApiMap("getDiscussionSocketToken").replace(
    ":projectId",
    projectId
  );
  const response = await api.post(url);
  return response.data;
}
